/* ---- Network Requests ---- */

/**
 * Create a tradein.
 *
 * @param {*} partnerUuid
 * @returns the created tradein
 */
async function createTradein(partnerUuid) {
  const query = `
    mutation tradeinCreate(
      $partnerUuid: String!
    ) {
      tradeinCreate(partnerUuid: $partnerUuid) {
        uuid
        state
        tradeinValueLow
        tradeinValueHigh
        tradeinItems {
          uuid
          catalogItemTitle
          parentSku
          itemImageUrl
          state
          tradeinValueLow
          tradeinValueLowDisplay
          tradeinValueHigh
          tradeinValueHighDisplay
          initiatedSubcategory
          isInitiatedConditionUnsellable
        }
      }
    }
  `;

  const param = 'query-tradeinCreate';
  try {
    const response = await fetchWithAppName(`${global.url}${param}`, {
      body: JSON.stringify({ query, variables: { partnerUuid } }),
    });

    const jsonData = await response.json();
    return jsonData.data.tradeinCreate;
  } catch (e) {
    console.error(e);
    return null;
  }
}


/**
 * Fetches a tradein.
 *
 * @param {*} tradeinUuid
 * @returns the requested tradein
 */
async function getTradein(tradeinUuid) {
  const query = `
    query tradein(
      $tradeinUuid: String!
    ) {
      tradein(tradeinUuid: $tradeinUuid) {
        uuid
        tradeinType
        state
        tradeinValueLow
        tradeinValueHigh
        trackingNumber
        trackingUrl
        carrier
        initiatedAt
        tradeinItems {
          uuid
          catalogItemTitle
          itemImageUrl
          parentSku
          state
          tradeinValueLow
          tradeinValueLowDisplay
          tradeinValueHigh
          tradeinValueHighDisplay
          initiatedCondition
          initiatedSubcategory
          isInitiatedConditionUnsellable
          tradeinPrice
          rejectedReason
        }
        giftcards {
          uuid
          amount
          balanceAmount
          lastFour
          createdAt
          email
          differentFromCurrentVendor
        }
      }
    }
  `;
  const param = 'query-tradein';
  try {
    const response = await fetchWithAppName(`${global.url}${param}`, {
      body: JSON.stringify({ query, variables: { tradeinUuid } }),
    });
  
    const jsonData = await response.json();
    return jsonData.data.tradein;
  } catch (e) {
    console.error(e);
    return null;
  }
}

/**
 * Returns the trade in rate for partner
 * 
 * @param {*} partnerUuid 
 * @returns the tradein shipping rate
 */
async function getShippingEstimate(partnerUuid) {
  const query = `
    query tradeinShippingEstimate(
      $partnerUuid: String!
    ) {
      tradeinShippingEstimate(partnerUuid: $partnerUuid) {
        shippingEstimate
        resultCode
      }
    }
  `;
  const param = 'query-tradeinShippingEstimate';
  try {
    const response = await fetchWithAppName(`${global.url}${param}`, {
      body: JSON.stringify({ query, variables: { partnerUuid } }),
    });

    const jsonData = await response.json();
    return jsonData.data.tradeinShippingEstimate;
  } catch (e) {
    console.error(e);
    return null;
  }

}

/**
 * Adds an item to the tradein.
 *
 * @param {*} variables
 * @param {String} variables.tradeinUuid
 * @param {String} variables.category
 * @param {String} variables.department
 * @param {String} variables.parentSku
 * @returns the tradein with the newly added item
 */
async function addItemToTradein(variables) {
  const query = `
    mutation tradeinAddItem(
      $tradeinUuid: String!
      $category: String,
      $color: String,
      $department: String,
      $parentSku: String,
      $initiatedCondition: String,
      $quotedValue: Int,
      $quotedValueDisplay: Int,
      $quotedValueLow: Int
      $quotedValueLowDisplay: Int
      $initiatedSubcategory: String
      $initiatedData: JSON
    ) {
      tradeinAddItem(
        tradeinUuid: $tradeinUuid,
        category: $category,
        color: $color,
        department: $department,
        parentSku: $parentSku,
        initiatedCondition: $initiatedCondition,
        quotedValue: $quotedValue,
        quotedValueDisplay: $quotedValueDisplay,
        quotedValueLow: $quotedValueLow
        quotedValueLowDisplay: $quotedValueLowDisplay
        initiatedSubcategory: $initiatedSubcategory
        initiatedData: $initiatedData
      ) {
        uuid
        state
        tradeinValueLow
        tradeinValueHigh
        tradeinItems {
          uuid
          catalogItemTitle
          parentSku
          itemImageUrl
          state
          tradeinValueLow
          tradeinValueLowDisplay
          tradeinValueHigh
          tradeinValueHighDisplay
          initiatedSubcategory
          isInitiatedConditionUnsellable
        }
      }
    }
  `;

  const param = 'query-tradeinAddItem';
  try {
    const response = await fetchWithAppName(`${global.url}${param}`, {
      body: JSON.stringify({ query, variables }),
    });

    const jsonData = await response.json();

    if(jsonData?.errors?.length) {
      throw new Error(jsonData.errors[0].message)
    }
    return jsonData.data.tradeinAddItem;
  } catch (e) {
    console.error(e);
    return null;
  }
}

/**
 * Removes an item from the tradein.
 *
 * @param {String} tradeinUuid
 * @param {String} tradeinItemUuid
 * @returns the tradein with the newly removed item
 */
async function removeItemFromTradein(tradeinUuid, tradeinItemUuid) {
  const query = `
    mutation tradeinRemoveItem(
      $tradeinUuid: String!
      $tradeinItemUuid: String!
    ) {
      tradeinRemoveItem(
        tradeinUuid: $tradeinUuid,
        tradeinItemUuid: $tradeinItemUuid
      ) {
        uuid
        state
        tradeinValueLow
        tradeinValueHigh
        tradeinItems {
          uuid
          catalogItemTitle
          parentSku
          itemImageUrl
          state
          tradeinValueLow
          tradeinValueLowDisplay
          tradeinValueHigh
          tradeinValueHighDisplay
          isInitiatedConditionUnsellable
        }
      }
    }
  `;

  const param = 'query-tradeinRemoveItem';
  try {
    const response = await fetchWithAppName(`${global.url}${param}`, {
      body: JSON.stringify({ query, variables: { tradeinUuid, tradeinItemUuid } }),
    });

    const jsonData = await response.json();
    return jsonData.data.tradeinRemoveItem;
  } catch (error) {
    console.error(e);
    return null;
  }
}

/**
 * Gets the number of items in a tradein.
 *
 * @param {*} tradeinUuid
 * @returns the requested tradein
 */
async function getTradeinItemsCount(tradeinUuid) {
  const query = `
    query tradein(
      $tradeinUuid: String!
    ) {
      tradein(tradeinUuid: $tradeinUuid) {
        tradeinItems {
          uuid
        }
      }
    }
  `;

  const param = 'query-tradein';
  try {
    const response = await fetchWithAppName(`${global.url}${param}`, {
      body: JSON.stringify({ query, variables: { tradeinUuid } }),
    });

    const jsonData = await response.json();
    return jsonData.data.tradein;
  } catch (e) {
    console.error(e);
    return null;
  }
}

/**
 * Submits the tradein.
 *
 * @param {*} variables
 * @param {String} variables.tradeinUuid
 * @param {String} variables.address1
 * @param {String} variables.address2
 * @param {String} variables.city
 * @param {String} variables.country
 * @param {String} variables.currencyCode
 * @param {String} variables.email
 * @param {Boolean} variables.emailConsent
 * @param {String} variables.firstName
 * @param {String} variables.lastName
 * @param {Boolean} variables.needsMitiKit
 * @param {String} variables.phone
 * @param {String} variables.postalCode
 * @param {String} variables.state
 * @param {String} variables.isSendbackOptIn
 * @returns the tradein with the newly added item
 */
async function submitTradeIn(variables) {
  const updateQuery = `
    mutation tradeinUpdate(
      $tradeinUuid: String!
      $address1: String,
      $address2: String,
      $city: String,
      $country: String,
      $currencyCode: String,
      $email: String,
      $emailConsent: Boolean,
      $firstName: String,
      $lastName: String,
      $needsMitiKit: Boolean,
      $phone: String,
      $postalCode: String,
      $state: String,
      $isSendbackOptIn: Boolean
    ) {
      tradeinUpdate(
        tradeinUuid: $tradeinUuid,
        address1: $address1,
        address2: $address2,
        city: $city,
        country: $country,
        currencyCode: $currencyCode,
        email: $email,
        emailConsent: $emailConsent,
        firstName: $firstName,
        lastName: $lastName,
        needsMitiKit: $needsMitiKit,
        phone: $phone,
        postalCode: $postalCode,
        state: $state,
        isSendbackOptIn: $isSendbackOptIn
      ) {
        uuid
        state
        tradeinValueLow
        tradeinValueHigh
        tradeinItems {
          uuid
          catalogItemTitle
          parentSku
          itemImageUrl
          state
          tradeinValueLow
          tradeinValueLowDisplay
          tradeinValueHigh
          tradeinValueHighDisplay
          isInitiatedConditionUnsellable
        }
      }
    }
  `;

  const submitQuery = `
    mutation tradeInSubmit(
      $tradeinUuid: String!
    ) {
      tradeinSubmit(
        tradeinUuid: $tradeinUuid
      ) {
        uuid
        state
        tradeinValueLow
        tradeinValueHigh
        tradeinItems {
          uuid
          catalogItemTitle
          parentSku
          itemImageUrl
          state
          tradeinValueLow
          tradeinValueLowDisplay
          tradeinValueHigh
          tradeinValueHighDisplay
          isInitiatedConditionUnsellable
        }
      }
    }
  `;
  const tradeinUuid = await getTradeinUuid();
  const updateParam = 'query-tradeinUpdate';
  const submitParam = 'query-tradeinSubmit';

  try {
    const updateResponse = await fetchWithAppName(`${global.url}${updateParam}`, {
      body: JSON.stringify({ query: updateQuery, variables: { ...variables, tradeinUuid } }),
    });

    await updateResponse.json();
    const submitResponse = await fetchWithAppName(`${global.url}${submitParam}`, {
      body: JSON.stringify({ query: submitQuery, variables: { ...variables, tradeinUuid } }),
    });

    const submitData = await submitResponse.json();
    return submitData;
  } catch (e) {
    console.error(e);
    return null;
  }
}

/**
 * Updates the html with the price based on whether it is a range or single dollar amount
 * 
 * @param {*} element the element to update
 * @param {*} param1 array with the low and high potential credit 
 */
function setPrice(element, [potentialCreditLow, potentialCreditHigh]) {
  const useSinglePrice = localStorage.getItem('tradein-use-single-tradein-pricing') === 'true';
  if (useSinglePrice) {
    setSinglePrice(element, potentialCreditHigh);
  } else {
    setPriceRange(element, [potentialCreditLow, potentialCreditHigh]);
  }
}

/**
 * Update html with a low - high price range
 * 
 * @param {*} element the element to update
 * @param {*} param1 array with the low and high potential credit to display
 */
function setPriceRange(element, [priceMin, priceMax]) {
  if (priceMax <= 0) {
    element.textContent = '-';
    return;
  }

  element.textContent = `${formatMoneyForTradein(priceMin)}-${formatMoneyForTradein(priceMax)}`;
}

/**
 * updates html with a single price
 * 
 * @param {*} element the element to update
 * @param {*} price the price to display
 */
function setSinglePrice(element, singlePrice) {
  if (singlePrice <= 0) {
    element.textContent = '-';
    return;
  }

  element.textContent =  formatMoneyForTradein(singlePrice);
}

function setShippingPrice(element, estimate) {
  const { calculatedAtCheckout, free } = TRADE_IN_CART_CONFIG;
  if (!estimate) element.textContent = '';
  if (estimate.resultCode === 'CALCULATED_AT_CHECKOUT') {
    element.textContent = calculatedAtCheckout;
  } else if (estimate.shippingEstimate === 0) {
    element.textContent = free;
  } else {
    setSinglePrice(element, estimate.shippingEstimate);
  }
}

/**
 * Calculates the potential credit for a list of items
 * @param {*} items list of items to calculate potential credit for
 * @returns the lowest and highest potential credit for all items
 */
function calculatePotentialCredit(items) {
  let potentialCreditLow = 0;
  let potentialCreditHigh = 0;
  let potentialCreditLowDisplay = 0;
  let potentialCreditHighDisplay = 0;
  items.forEach(item => {
    potentialCreditLow += item.tradeinValueLow;
    potentialCreditHigh += item.tradeinValueHigh;
    potentialCreditLowDisplay += item.tradeinValueLowDisplay;
    potentialCreditHighDisplay += item.tradeinValueHighDisplay;
  });
  return {
    potentialCreditLow,
    potentialCreditHigh,
    potentialCreditLowDisplay,
    potentialCreditHighDisplay
  };
}

/**
 * Calculate the giftcard amount based on the potential credit and shipping estimate
 * @param {*} param0 
 * @returns returns the low and high giftcard estimate 
 */
function calculateGiftcardEstimate({ potentialCreditLow, potentialCreditHigh, shippingEstimate }) {
  const shippingEstimateValue = shippingEstimate?.shippingEstimate || 0;
  return {
    giftcardEstimateLow: potentialCreditLow - shippingEstimateValue,
    giftcardEstimateHigh: potentialCreditHigh - shippingEstimateValue,
  };
}

async function getTradeinUuid() {
  // create a new tradein if the user hasn't already started one
  let tradeinUuid = localStorage.getItem('tradeinUuid');
  if (!tradeinUuid) {
    const { uuid } = await createTradein(global.partnerUuid);
    localStorage.setItem('tradeinUuid', uuid);
    tradeinUuid = uuid;
  }
  return tradeinUuid;
}

async function giftcardResendEmail(giftcardUuid) {
  const query = `
    mutation giftcardResendEmail(
      $giftcardUuid: String!
    ) {
      giftcardResendEmail(
        giftcardUuid: $giftcardUuid,
      ) {
        uuid
      }
    }
  `;

  const param = 'query-giftcardResendEmail';
  try {
    const response = await fetchWithAppName(`${global.url}${param}`, {
      body: JSON.stringify({ query, variables: { giftcardUuid } }),
    });

    const jsonData = await response.json();

    if(jsonData?.errors?.length) {
      throw new Error(jsonData.errors[0].message)
    }
    return jsonData.data.giftcardResendEmail;
  } catch (e) {
    console.error(e);
    return null;
  }
};

/**
 * Get the url of the packing slip in pdf.
 *
 * @param {*} tradeinUuid
 * @returns true
 */
async function generatePackingSlip(tradeinUuid) {
  const query = `
    mutation tradeinGeneratePackingSlip(
      $tradeinUuid: String!
    ) {
      tradeinGeneratePackingSlip(tradeinUuid: $tradeinUuid)
    }
  `;
  const param = 'query-tradeinGeneratePackingSlip';
  try {
    const response = await fetchWithAppName(`${global.url}${param}`, {
      body: JSON.stringify({ query, variables: { tradeinUuid } }),
    });
    const jsonData = await response.json();
    return jsonData?.data?.tradeinGeneratePackingSlip;

  } catch(e) {
    console.error(e);
    return null;
  }
}

/**
 * Get the url of the shippping label in pdf.
 *
 * @param {*} tradeinUuid
 * @returns true
 */
async function generateShippingLabel(tradeinUuid) {
  const query = `
    mutation tradeinGenerateShippingLabel(
      $tradeinUuid: String!
    ) {
      tradeinGenerateShippingLabel(tradeinUuid: $tradeinUuid)
    }
  `;
  const param = 'query-tradeinGenerateShippingLabel';
  try {
    const response = await fetchWithAppName(`${global.url}${param}`, {
      body: JSON.stringify({ query, variables: { tradeinUuid } }),
    });
    const jsonData = await response.json();
    return jsonData?.data?.tradeinGenerateShippingLabel;

  } catch(e) {
    console.error(e);
    return null;
  }
}

async function addItemAndRedirectToCart() {
  const tradeinUuid = await getTradeinUuid();
  const currentTradeinItem = JSON.parse(sessionStorage.getItem('currentTradeinItem'));
  await addItemToTradein({
    tradeinUuid,
    parentSku: currentTradeinItem.parentSku || '<unknown>',
    initiatedCondition: currentTradeinItem.recommendedGrade,
    color: currentTradeinItem.colorCode || null,
    quotedValue: currentTradeinItem.tradeinValue,
    quotedValueDisplay: currentTradeinItem.displayTradeinValue,
    quotedValueLow: currentTradeinItem.tradeinValue,
    quotedValueLowDisplay: currentTradeinItem.displayTradeinValue,
    department: currentTradeinItem.department,
    category: currentTradeinItem.category,
    initiatedSubcategory: currentTradeinItem.subcategory,
    initiatedData: currentTradeinItem.data,
  });
  sessionStorage.removeItem('currentTradeinItem');
  window.location = `${Shopify.routes.root}pages/trade-in-cart`;
};
